import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createChart } from 'lightweight-charts';
import PropTypes from 'prop-types';
import { BASE_URL } from '../../apiConstant';
import { useGlobal } from '../../utils/contexts/GlobalContext';
import ButtonGroup from '../details/ButtonGroup';

// Fetch members data function
const fetchMembersData = async (pageParam = 1, period, startDate, endDate, pageSize) => {
    const response = await fetch(
        `${BASE_URL}/total-member-count?period=${period}&order_by_date=desc&paginate=true&startDate=${startDate}&endDate=${endDate}&page_size=${pageSize}&page=${pageParam}`
    );
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    return await response.json();
};

// Create initial placeholder data for the last 10 days
const createInitialPlaceholderData = () => {
    return [
        {
            "time": 1726617600,
            "value": 0
        },
        {
            "time": 1726704000,
            "value": 0
        }
    ]
};

const OverallMemberChart = ({ period, startDate, endDate }) => {
    const [selectedMetric, setSelectedMetric] = useState('10');
    const { state } = useGlobal(); // Get global state and dispatch
    const isDarkMode = state.isDarkMode; // Access dark mode state from global context
    const chartContainerRef = useRef();
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const lastFetchedTimeRef = useRef(null);
    const previousVisibleRangeRef = useRef(null);

    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useInfiniteQuery({
        queryKey: ['membersDataOverall', { period, startDate, endDate, selectedMetric }],
        queryFn: ({ pageParam = 1 }) => fetchMembersData(pageParam, period, startDate, endDate, selectedMetric),
        getNextPageParam: (lastPage) => (lastPage.pagination.hasNextPage ? lastPage.pagination.currentPage + 1 : undefined),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    // const chartData = React.useMemo(() => {
    //     if (!data) return [];
    //     return data.pages.flatMap(page =>
    //         page.data.map(item => ({
    //             time: new Date(item.period_name).getTime() / 1000,
    //             value: item['total_member_count'],
    //         }))
    //     ).sort((a, b) => a.time - b.time);
    // }, [data, selectedMetric]);

    const chartData = React.useMemo(() => {
        if (!data) return [];
        return data.pages.flatMap(page =>
            page.data.map(item => {
                const [year, month, day] = item.period_name.split('-').map(Number);
                const time = Date.UTC(year, month - 1, day) / 1000; // Convert to Unix timestamp in seconds
                return {
                    time,
                    value: item['total_member_count'],
                };
            })
        ).sort((a, b) => a.time - b.time);
    }, [data, selectedMetric]);

    const initialData = React.useMemo(() => createInitialPlaceholderData(), []);

    const handleVisibleRangeChange = useCallback((logicalRange) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            const currentTime = Date.now();
            const timeSinceLastFetch = currentTime - (lastFetchedTimeRef.current || 0);

            if (hasNextPage && !isFetchingNextPage && timeSinceLastFetch > 2000) {
                const visibleRange = chartRef.current.timeScale().getVisibleRange();
                const dataRange = chartRef.current.timeScale().getVisibleLogicalRange();

                if (visibleRange && dataRange) {
                    // Check if user has scrolled left
                    if (previousVisibleRangeRef.current) {
                        const isScrollingLeft = logicalRange.from < previousVisibleRangeRef.current.from;

                        if (isScrollingLeft) {
                            const visibleDataPercentage = (dataRange.to - dataRange.from) / (visibleRange.to - visibleRange.from);

                            if (visibleDataPercentage < 0.7) {
                                console.log('Fetching more data');
                                lastFetchedTimeRef.current = currentTime;
                                fetchNextPage();
                            }
                        }
                    }

                    // Update the previous visible range
                    previousVisibleRangeRef.current = logicalRange;
                }
            }
        }, 200);
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const chartOptions = {
            layout: {
                textColor: !isDarkMode ? '#0d1217' : '#dfe5ec',
                background: {
                    color: isDarkMode ? '#0d1217' : 'white',
                    text: !isDarkMode ? '#0d1217' : '#dfe5ec',
                },
            },
        };

        if (!chartRef.current) {
            chartRef.current = createChart(chartContainerRef.current, {
                ...chartOptions,
                width: chartContainerRef.current.clientWidth,
                height: chartContainerRef.current.clientHeight,
                watermark: {
                    visible: false, // Ensures the watermark is hidden
                },
            });

            seriesRef.current = chartRef.current.addBaselineSeries({
                baseValue: { type: 'price', price: 0 },
                topLineColor: 'rgba(38, 166, 154, 1)',
                topFillColor1: 'rgba(38, 166, 154, 0.28)',
                topFillColor2: 'rgba(38, 166, 154, 0.05)',
                bottomLineColor: 'rgba(239, 83, 80, 1)',
                bottomFillColor1: 'rgba(239, 83, 80, 0.05)',
                bottomFillColor2: 'rgba(239, 83, 80, 0.28)',
                priceFormat: {
                    type: 'custom',
                    formatter: price => price.toFixed(0), // Format without decimals
                },
            });

            chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
        }

        // Use initial placeholder data before real data comes in
        seriesRef.current.setData(initialData);

        // Once data is available, update the chart
        if (chartData.length > 0) {
            seriesRef.current.setData(chartData);
            chartRef.current.timeScale().fitContent();
        }

        const removeLogo = () => {
            const logo = document.getElementById('tv-attr-logo');
            if (logo) {
                logo.style.display = 'none'; // Hide the logo
            }
        };
        // Call after a small delay to ensure the chart is rendered
        const timer = setTimeout(removeLogo, 0);

        return () => {
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
                seriesRef.current = null;
            }
            clearTimeout(timer);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [chartData, initialData, handleVisibleRangeChange, isDarkMode]);

    const handleMetricChange = (metric) => {
        setSelectedMetric(metric);
        refetch(); // Refetch data with the new metric
    };

    const buttons = [
        { id: '10', label: '10D', key: '10' },
        { id: '20', label: '20D', key: '20' },
        { id: '30', label: '30D', key: '30' },
    ];

    return (
        <>
            <div className="md:p-4 relative min-w-[50vw] h-full container max-w-[100vw] ring-gray-200 dark:ring-moon-700 ring-2 py-1.5 px-2 rounded-xl bg-white dark:bg-moon-900">
                <div className="w-full h-full">
                    <div className='w-full h-full flex justify-center md:justify-between items-center'>
                        <ButtonGroup buttons={buttons} defaultActive="10" onClick={handleMetricChange} />
                        <div
                            data-view-component="true"
                            className="hidden md:inline-flex mt-0.5 ml-1 mr-3 2lg:mr-0  items-center px-1.5 py-0.5  rounded-md"
                        >
                            <div
                                data-view-component="true"
                                className=" leading-4 text-gray-700 dark:text-moon-200 font-medium"
                            >
                                Members Chart
                            </div>
                        </div>
                    </div>

                    <div className="">
                        <div className='mt-2' ref={chartContainerRef} style={{ position: 'relative', width: '100%', height: '200px' }} />

                        {(isFetchingNextPage || isLoading) && (
                            <div className="mt-4 flex justify-center">
                                <span className='loader'></span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

OverallMemberChart.propTypes = {
    period: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
};

export default OverallMemberChart;